import classNames from 'classnames'
import { format, formatDistanceToNow, isValid } from 'date-fns'
import { Fragment, useReducer } from 'react'
import { useInterval } from 'react-use'

import { useAppSelector } from '../../store'

interface Props {
  commitsDirection?: 'row' | 'column'
}

export function AppVersion(props: Props): JSX.Element {
  const commit = useAppSelector(state => state.app.commit)

  const clientCommitRef = window.env.COMMIT_REF
  let clientCommitDate: Date | undefined = new Date(window.env.COMMIT_DATE ?? '')

  const serverCommitRef = commit.ref
  let serverCommitDate: Date | undefined = new Date(commit.date ?? '')

  if (!isValid(clientCommitDate)) {
    clientCommitDate = undefined
  }

  if (!isValid(serverCommitDate)) {
    serverCommitDate = undefined
  }

  const [, update] = useReducer(x => x * -1, 1)

  useInterval(() => {
    update()
  }, 60 * 1000)

  const isSameCommit =
    (clientCommitRef && !serverCommitRef) ||
    (serverCommitRef && !clientCommitRef) ||
    (clientCommitRef && serverCommitRef && clientCommitRef === serverCommitRef)

  return (
    <div className='flex gap-2 text-sm text-gray-400'>
      {isSameCommit && (
        <Commit
          commitRef={clientCommitRef ?? serverCommitRef ?? ''}
          date={clientCommitDate ?? serverCommitDate ?? new Date('')}
          href={`https://gitlab.com/l2874/lucent-finance/payments/-/commit/${clientCommitRef ?? serverCommitRef ?? ''}`}
        />
      )}

      {!isSameCommit && (
        <div
          className={classNames('flex justify-center items-center', {
            'flex-col gap-1': props.commitsDirection === 'column',
            'gap-2': props.commitsDirection === 'row'
          })}
        >
          <Commits
            direction={props.commitsDirection ?? 'row'}
            commits={[
              {
                module: 'frontend',
                commitRef: clientCommitRef ?? '',
                date: clientCommitDate ?? new Date(''),
                href: `https://gitlab.com/l2874/lucent-finance/payments/-/commit/${clientCommitRef ?? ''}`
              },
              {
                module: 'backend',
                commitRef: serverCommitRef ?? '',
                date: serverCommitDate ?? new Date(''),
                href: `https://gitlab.com/l2874/lucent-finance/payments/-/commit/${serverCommitRef ?? ''}`
              }
            ].filter(v => typeof v.commitRef === 'string' && v.commitRef.length > 0)}
          />

          <button
            type='button'
            className='text-sm font-semibold text-orange-500 border animate-pulse hover:text-orange-600'
            title='Version mismatch, click to refresh'
            onClick={() => {
              window.location.reload()
            }}
          >
            Refresh!
          </button>
        </div>
      )}
    </div>
  )
}

interface CommitProps {
  commitRef: string
  date: Date
  module?: string
  href?: string
}

function Commit(props: CommitProps): JSX.Element {
  const Comp: React.ElementType = typeof props.href === 'string' ? 'a' : 'span'

  return (
    <div className='flex gap-1 justify-center items-center'>
      {typeof props.module === 'string' && <span>{props.module}: </span>}

      <Comp
        className='hover:underline underline-offset-4'
        href={props.href}
        rel='noopener noreferrer'
        target='_blank'
      >
        <code>{props.commitRef.slice(0, 7)}</code>
      </Comp>

      {isValid(props.date) && (
        <>
          {' | '}

          <button
            type='button'
            title={safeFormatDate(props.date)}
            // className='p-1 -m-1 cursor-pointer'
            className='p-1 -m-1 cursor-pointer'
            onClick={() => {
              void navigator.clipboard.writeText(
                `${format(props.date, 'yyyy-MM-dd')} | ${props.commitRef}`
              )
            }}
          >
            {formatDistanceToNow(props.date)}
          </button>
        </>
      )}
    </div>
  )
}

interface CommitsProps {
  commits: CommitProps[]
  direction: 'row' | 'column'
}

function Commits(props: CommitsProps): JSX.Element {
  if (props.direction === 'row') {
    return (
      <>
        {props.commits.map((commit, index) => (
          <Fragment key={index}>
            {index > 0 && <span>{'//'}</span>}

            <Commit {...commit} />
          </Fragment>
        ))}
      </>
    )
  }

  return (
    <div className='flex flex-col'>
      {props.commits.map((v, i) => (
        <Commit {...v} key={i} />
      ))}
    </div>
  )
}

function safeFormatDate(date: Date): string | undefined {
  return isValid(date) ? format(date, 'MM/dd/yyyy HH:mm') : undefined
}
